import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { X, Loader2, AlertTriangle, MapPin, Check, X as XIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Profile } from '@/lib/types/core/profile';
import { appsUserApiRouterUpdateProfile } from '@/lib/api';
import { reverseGeocode, geocodeAddress } from '@/lib/geocoding';

interface ProfileEditFormProps {
  profile: Profile;
  onSave: (updatedProfile: Profile) => void;
  onCancel: () => void;
}

type FormData = {
  preferred_riding_style: string;
  location: string;
  bio: string;
};

interface LocationInputProps {
  onLocationChange: (lat: number, lng: number) => void;
  onGeocodingStatus: (isValid: boolean) => void;
  onLocationAddressChange: (address: string) => void;
  initialAddress?: string;
}

const LocationInput = ({ onLocationChange, onGeocodingStatus, onLocationAddressChange, initialAddress }: LocationInputProps) => {
  const [address, setAddress] = useState(initialAddress || '');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingStatus, setGeocodingStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    setGeocodingStatus('idle');
    onGeocodingStatus(false);

    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer
    const timer = setTimeout(() => {
      if (newAddress.trim()) {
        performGeocoding(newAddress);
      }
    }, 1000);

    setDebounceTimer(timer);
  };

  const performGeocoding = async (addressToGeocode: string) => {
    setIsGeocoding(true);
    setGeocodingStatus('idle');

    try {
      const results = await geocodeAddress(addressToGeocode, { limit: 1 });
      
      if (results.length > 0) {
        const [lng, lat] = results[0].coordinates;
        onLocationChange(lat, lng);
        onLocationAddressChange(addressToGeocode); // Update address in form
        setGeocodingStatus('success');
        onGeocodingStatus(true);
      } else {
        setGeocodingStatus('error');
        onGeocodingStatus(false);
        toast({
          title: "Address not found",
          description: "Could not find coordinates for this address.",
          variant: "destructive"
        });
      }
    } catch (error) {
      setGeocodingStatus('error');
      onGeocodingStatus(false);
      toast({
        title: "Geocoding failed",
        description: "Failed to convert address to coordinates.",
        variant: "destructive"
      });
    } finally {
      setIsGeocoding(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: "Geolocation unavailable",
        description: "Your browser doesn't support geolocation.",
        variant: "destructive"
      });
      return;
    }
    
    setIsGeocoding(true);
    setGeocodingStatus('idle');
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        onLocationChange(latitude, longitude);
        
        // Reverse geocode to get address
        try {
          const results = await reverseGeocode([longitude, latitude], { limit: 1 });
          if (results.length > 0) {
            setAddress(results[0].placeName);
            onLocationAddressChange(results[0].placeName); // Update address in form
          }
        } catch (error) {
          console.error("Failed to get address:", error);
        }
        
        setGeocodingStatus('success');
        onGeocodingStatus(true);
        setIsGeocoding(false);
      },
      (error) => {
        setIsGeocoding(false);
        setGeocodingStatus('error');
        onGeocodingStatus(false);
        toast({
          title: "Location error",
          description: error.message,
          variant: "destructive"
        });
      }
    );
  };

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return (
    <div>
      <label className="block text-sm font-medium mb-1">
        Home Base
      </label>
      <div className="flex flex-col md:flex-row gap-2">
        <div className="flex-1 relative">
          <Input
            value={address}
            onChange={handleAddressChange}
            placeholder="Enter address (e.g., 123 Main St, Nashville, TN)"
            className="w-full px-4 py-2.5 pr-10 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isGeocoding && (
              <Loader2 size={16} className="animate-spin text-adv-orange" />
            )}
            {geocodingStatus === 'success' && !isGeocoding && (
              <Check size={16} className="text-green-500" />
            )}
            {geocodingStatus === 'error' && !isGeocoding && (
              <XIcon size={16} className="text-red-500" />
            )}
          </div>
        </div>
        <Button
          type="button"
          onClick={getCurrentLocation}
          disabled={isGeocoding}
          className="whitespace-nowrap hover:bg-adv-mediumGray"
        >
          {isGeocoding ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Getting Location...
            </>
          ) : (
            <>
              <MapPin size={16} className="mr-2" />
              Use Current Location
            </>
          )}
        </Button>
      </div>
      <p className="mt-1 text-xs text-adv-lightGray">
        Enter your city/state or use your current location
      </p>
    </div>
  );
};

const ProfileEditForm = ({ profile, onSave, onCancel }: ProfileEditFormProps) => {
  const [isLocationValid, setIsLocationValid] = useState(false);
  const [geoLocation, setGeoLocation] = useState<{ latitude: number; longitude: number } | null>(
    profile.geo_location
  );
  const [locationAddress, setLocationAddress] = useState(profile.location || '');
  const { toast } = useToast();
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<FormData>({
    defaultValues: {
      preferred_riding_style: profile.preferred_riding_style || '',
      location: profile.location || '',
      bio: profile.bio || ''
    }
  });

  const handleLocationChange = useCallback((lat: number, lng: number) => {
    setGeoLocation({ latitude: lat, longitude: lng });
  }, []);

  const handleLocationAddressChange = useCallback((address: string) => {
    setLocationAddress(address);
  }, []);

  const handleGeocodingStatus = useCallback((isValid: boolean) => {
    setIsLocationValid(isValid);
  }, []);

  const onSubmit = async (data: FormData) => {
    try {
      const updateData = {
        ...data,
        location: locationAddress, // Use the state value instead of form data
        geo_location: geoLocation
      };

      const response = await appsUserApiRouterUpdateProfile(updateData);
      // Convert response to Profile type
      const updatedProfile: Profile = {
        ...response.data.user,
        geo_location: geoLocation
      };
      onSave(updatedProfile);
      
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
        variant: "default"
      });
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update profile.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="glass-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Edit Profile</h2>
        <Button
          variant="outline"
          onClick={onCancel}
          className="text-adv-lightGray hover:text-white"
        >
          <X size={20} />
        </Button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1">
            Riding Style
          </label>
          <select
            {...register('preferred_riding_style', { required: "Riding style is required" })}
            className="w-full px-4 py-2.5 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
          >
            <option value="">Select riding style</option>
            <option value="Street">Street</option>
            <option value="Seeking Off-road">Adventure/Dual Sport</option>
            <option value="Fully Off-road">Dirt</option>
          </select>
          {errors.preferred_riding_style && (
            <p className="mt-1 text-sm text-red-500">{errors.preferred_riding_style.message}</p>
          )}
        </div>

        <LocationInput 
          onLocationChange={handleLocationChange}
          onGeocodingStatus={handleGeocodingStatus}
          onLocationAddressChange={handleLocationAddressChange}
          initialAddress={profile.location || ''}
        />

        <div>
          <label className="block text-sm font-medium mb-1">
            Bio
          </label>
          <Textarea
            {...register('bio')}
            placeholder="Tell us about yourself and your riding experience..."
            className="w-full px-4 py-2.5 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all min-h-[100px]"
          />
          {errors.bio && (
            <p className="mt-1 text-sm text-red-500">{errors.bio.message}</p>
          )}
        </div>

        {/* Hidden fields for geo_location */}
        <input type="hidden" name="geo_location_lat" value={geoLocation?.latitude || ''} />
        <input type="hidden" name="geo_location_lng" value={geoLocation?.longitude || ''} />

        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-adv-orange hover:bg-adv-orange/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileEditForm; 